/**
 * Rate Limiter Utility
 * 
 * Implements sliding window rate limiting for authentication and API requests.
 * Critical for maritime applications where:
 * - Ships have limited bandwidth (satellite internet)
 * - Brute force attacks must be prevented
 * - ISPS (International Ship and Port Facility Security) compliance requires access control
 * 
 * Features:
 * - Sliding window algorithm (more accurate than fixed window)
 * - Configurable limits and time windows
 * - Persistent storage (survives page reload)
 * - Automatic cleanup of old entries
 * - Support for multiple rate limit types (login, API, etc.)
 * 
 * @module rateLimiter
 */

import { createLogger } from './logger';

const logger = createLogger('RateLimiter');

/**
 * Configuration for a rate limit
 */
export interface RateLimitConfig {
  /** Maximum number of attempts allowed */
  maxAttempts: number;
  /** Time window in milliseconds */
  windowMs: number;
  /** Optional custom error message */
  message?: string;
}

/**
 * Attempt record stored in localStorage
 */
interface AttemptRecord {
  timestamp: number;
  identifier: string; // e.g., email, IP, user ID
}

/**
 * Rate limit result
 */
export interface RateLimitResult {
  /** Whether the request is allowed */
  allowed: boolean;
  /** Number of attempts remaining (0 if blocked) */
  remaining: number;
  /** When the rate limit will reset (timestamp) */
  resetAt: number;
  /** Time until reset in milliseconds */
  resetInMs: number;
  /** Total attempts in current window */
  totalAttempts: number;
}

/**
 * Rate limiter types for different operations
 */
export enum RateLimitType {
  LOGIN = 'login',
  API = 'api',
  PASSWORD_RESET = 'password_reset',
  CAPTCHA_TRIGGER = 'captcha_trigger',
}

/**
 * Default rate limit configurations
 */
const DEFAULT_CONFIGS: Record<RateLimitType, RateLimitConfig> = {
  [RateLimitType.LOGIN]: {
    maxAttempts: 5,
    windowMs: 15 * 60 * 1000, // 15 minutes
    message: 'Too many login attempts. Please try again in {time}.',
  },
  [RateLimitType.API]: {
    maxAttempts: 100,
    windowMs: 60 * 1000, // 1 minute
    message: 'API rate limit exceeded. Please try again in {time}.',
  },
  [RateLimitType.PASSWORD_RESET]: {
    maxAttempts: 3,
    windowMs: 60 * 60 * 1000, // 1 hour
    message: 'Too many password reset attempts. Please try again in {time}.',
  },
  [RateLimitType.CAPTCHA_TRIGGER]: {
    maxAttempts: 3,
    windowMs: 15 * 60 * 1000, // 15 minutes (triggers CAPTCHA after 3 failed logins)
    message: 'Multiple failed attempts detected. CAPTCHA required.',
  },
};

/**
 * RateLimiter class
 * 
 * Usage:
 * ```typescript
 * const limiter = new RateLimiter(RateLimitType.LOGIN);
 * 
 * // Check before allowing action
 * const result = limiter.check('user@example.com');
 * if (!result.allowed) {
 *   throw new Error(`Rate limit exceeded. Try again in ${formatDuration(result.resetInMs)}`);
 * }
 * 
 * // Record attempt after action
 * limiter.record('user@example.com');
 * ```
 */
export class RateLimiter {
  private config: RateLimitConfig;
  private storageKey: string;

  /**
   * Create a new rate limiter
   * @param type - Type of rate limit (LOGIN, API, etc.)
   * @param customConfig - Optional custom configuration (overrides defaults)
   */
  constructor(type: RateLimitType, customConfig?: Partial<RateLimitConfig>) {
    this.config = {
      ...DEFAULT_CONFIGS[type],
      ...customConfig,
    };
    this.storageKey = `rate_limit_${type}`;
    
    // Cleanup old entries on initialization
    this.cleanup();
  }

  /**
   * Check if an action is allowed for the given identifier
   * @param identifier - Unique identifier (email, user ID, etc.)
   * @returns Rate limit result with allowed status and metadata
   */
  check(identifier: string): RateLimitResult {
    const attempts = this.getAttempts(identifier);
    const now = Date.now();
    const windowStart = now - this.config.windowMs;

    // Filter attempts within the current window
    const recentAttempts = attempts.filter(a => a.timestamp > windowStart);
    
    // Prune expired attempts from ALL identifiers in storage (periodic cleanup)
    // This prevents localStorage growth even if record() isn't called
    if (attempts.length > recentAttempts.length) {
      this.pruneExpiredAttempts(now);
    }
    
    const totalAttempts = recentAttempts.length;
    const remaining = Math.max(0, this.config.maxAttempts - totalAttempts);
    const allowed = totalAttempts < this.config.maxAttempts;

    // Calculate reset time (oldest attempt + window)
    const oldestAttempt = recentAttempts.length > 0
      ? Math.min(...recentAttempts.map(a => a.timestamp))
      : now;
    const resetAt = oldestAttempt + this.config.windowMs;
    const resetInMs = Math.max(0, resetAt - now);

    return {
      allowed,
      remaining,
      resetAt,
      resetInMs,
      totalAttempts,
    };
  }

  /**
   * Record an attempt for the given identifier
   * @param identifier - Unique identifier (email, user ID, etc.)
   */
  record(identifier: string): void {
    const attempts = this.getAttempts();
    const now = Date.now();
    const windowStart = now - this.config.windowMs;

    // Filter out expired attempts before adding new one (prevent unbounded growth)
    const recentAttempts = attempts.filter(a => a.timestamp > windowStart);

    // Add new attempt
    recentAttempts.push({
      timestamp: now,
      identifier,
    });

    // Save pruned list to storage
    this.saveAttempts(recentAttempts);
  }

  /**
   * Reset all attempts for a specific identifier (e.g., after successful login)
   * @param identifier - Unique identifier to reset
   */
  reset(identifier: string): void {
    const attempts = this.getAttempts();
    const filtered = attempts.filter(a => a.identifier !== identifier);
    this.saveAttempts(filtered);
  }

  /**
   * Reset all attempts for this rate limiter
   */
  resetAll(): void {
    this.saveAttempts([]);
  }

  /**
   * Get formatted error message with time remaining
   * @param resetInMs - Time remaining in milliseconds
   * @returns Formatted error message
   */
  getErrorMessage(resetInMs: number): string {
    const timeStr = this.formatDuration(resetInMs);
    return this.config.message?.replace('{time}', timeStr) || 
      `Rate limit exceeded. Try again in ${timeStr}.`;
  }

  /**
   * Format duration in human-readable format
   * @param ms - Duration in milliseconds
   * @returns Formatted string (e.g., "5 minutes", "2 hours")
   */
  private formatDuration(ms: number): string {
    const seconds = Math.ceil(ms / 1000);
    const minutes = Math.ceil(seconds / 60);
    const hours = Math.ceil(minutes / 60);

    if (hours > 1) {
      return `${hours} hour${hours > 1 ? 's' : ''}`;
    }
    if (minutes > 1) {
      return `${minutes} minute${minutes > 1 ? 's' : ''}`;
    }
    return `${seconds} second${seconds > 1 ? 's' : ''}`;
  }

  /**
   * Get all attempts from storage
   * @param identifier - Optional filter by identifier
   * @returns Array of attempt records
   */
  private getAttempts(identifier?: string): AttemptRecord[] {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (!stored) return [];

      const attempts: AttemptRecord[] = JSON.parse(stored);
      
      // Filter by identifier if provided
      if (identifier) {
        return attempts.filter(a => a.identifier === identifier);
      }

      return attempts;
    } catch (error) {
      logger.error('Failed to read attempts from storage', error);
      return [];
    }
  }

  /**
   * Save attempts to storage
   * @param attempts - Array of attempt records to save
   */
  private saveAttempts(attempts: AttemptRecord[]): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(attempts));
    } catch (error) {
      logger.error('Failed to save attempts to storage', error);
      // Silently fail - rate limiting will be less effective but won't break functionality
    }
  }

  /**
   * Cleanup old attempts outside the current window
   * Runs automatically on initialization and can be called manually
   */
  cleanup(): void {
    const attempts = this.getAttempts();
    const now = Date.now();
    const windowStart = now - this.config.windowMs;

    // Keep only recent attempts
    const recentAttempts = attempts.filter(a => a.timestamp > windowStart);

    // Only save if we removed something
    if (recentAttempts.length < attempts.length) {
      this.saveAttempts(recentAttempts);
    }
  }

  /**
   * Prune expired attempts from storage (internal helper for automatic cleanup)
   * @param now - Current timestamp
   * @private
   */
  private pruneExpiredAttempts(now: number): void {
    const attempts = this.getAttempts();
    const windowStart = now - this.config.windowMs;

    // Keep only recent attempts
    const recentAttempts = attempts.filter(a => a.timestamp > windowStart);

    // Save pruned list (no need to check length - we know there are expired entries)
    this.saveAttempts(recentAttempts);
  }

  /**
   * Get current configuration
   */
  getConfig(): RateLimitConfig {
    return { ...this.config };
  }
}

/**
 * Utility function to format rate limit error for user display
 * @param result - Rate limit result
 * @param limiter - Rate limiter instance
 * @returns User-friendly error message
 */
export function formatRateLimitError(result: RateLimitResult, limiter: RateLimiter): string {
  return limiter.getErrorMessage(result.resetInMs);
}

/**
 * Utility function to get remaining attempts message
 * @param remaining - Number of attempts remaining
 * @returns User-friendly message
 */
export function getRemainingAttemptsMessage(remaining: number): string {
  if (remaining === 0) {
    return 'Account temporarily locked';
  }
  if (remaining === 1) {
    return '1 attempt remaining';
  }
  return `${remaining} attempts remaining`;
}

/**
 * Global rate limiter instances (singleton pattern)
 * These are shared across the application to maintain consistent state
 */
export const loginRateLimiter = new RateLimiter(RateLimitType.LOGIN);
export const apiRateLimiter = new RateLimiter(RateLimitType.API);
export const passwordResetRateLimiter = new RateLimiter(RateLimitType.PASSWORD_RESET);
export const captchaRateLimiter = new RateLimiter(RateLimitType.CAPTCHA_TRIGGER);

/**
 * Check if CAPTCHA should be triggered based on failed login attempts
 * @param identifier - User identifier (email)
 * @returns True if CAPTCHA should be shown
 */
export function shouldTriggerCaptcha(identifier: string): boolean {
  const result = captchaRateLimiter.check(identifier);
  return !result.allowed;
}

/**
 * Record failed login attempt for CAPTCHA tracking
 * @param identifier - User identifier (email)
 */
export function recordFailedLoginForCaptcha(identifier: string): void {
  captchaRateLimiter.record(identifier);
}

/**
 * Reset CAPTCHA counter after successful login
 * @param identifier - User identifier (email)
 */
export function resetCaptchaCounter(identifier: string): void {
  captchaRateLimiter.reset(identifier);
}
